/* eslint-disable lingui/text-restrictions */
/* eslint-disable lingui/no-unlocalized-strings */

import { t } from "@lingui/macro";
import {
  ArrowRight,
  ListChecks,
  ShieldCheck,
  Target,
} from "@phosphor-icons/react";
import { Button } from "@reactive-resume/ui";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

import { useAuthStore } from "@/client/stores/auth";

const scoreDetails = [
  {
    icon: <Target weight="duotone" className="h-6 w-6" />,
    label: "Keyword Coverage",
    desc: "Identifies missing keywords that ATS systems scan for in your target role",
  },
  {
    icon: <ListChecks weight="duotone" className="h-6 w-6" />,
    label: "Content Quality",
    desc: "Scores bullet points on the action-verb + task + measurable-result formula",
  },
  {
    icon: <ShieldCheck weight="duotone" className="h-6 w-6" />,
    label: "Format & Structure",
    desc: "Checks parsing compatibility with Greenhouse, Lever, Workday, and Taleo",
  },
];

export const FeatureATSScoreSection = () => {
  const isLoggedIn = useAuthStore((s) => !!s.user);

  return (
    <section
      id="ats-score"
      className="relative overflow-hidden bg-secondary-accent/50 py-24 sm:py-32"
    >
      <div className="container">
        <motion.div
          className="mx-auto max-w-3xl text-center"
          viewport={{ once: true }}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
        >
          <p className="text-sm font-semibold uppercase tracking-wider text-green-600">
            {t`ATS Optimizer`}
          </p>
          <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
            {t`Check Your ATS Resume Score Instantly`}
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-foreground/60">
            85% of companies use Applicant Tracking Systems to filter resumes before a human ever
            reads them. CareerBldr scores your resume against 2026 hiring standards and tells you
            exactly what to fix — keyword gaps, bullet quality, formatting issues, and more.
          </p>
        </motion.div>

        {/* Screenshot — centered, properly sized */}
        <motion.div
          className="mx-auto mt-12 flex justify-center"
          viewport={{ once: true }}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0, transition: { delay: 0.1 } }}
        >
          <img
            src="/screenshots/ats-score.png"
            alt="ATS Score Checker showing a 98/100 score with actionable keyword and formatting suggestions"
            loading="lazy"
            decoding="async"
            className="w-full max-w-sm rounded-xl shadow-2xl ring-1 ring-foreground/10"
          />
        </motion.div>

        <motion.div
          className="mt-10 text-center"
          viewport={{ once: true }}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1, transition: { delay: 0.2 } }}
        >
          <Button asChild size="lg">
            <Link to={isLoggedIn ? "/dashboard" : "/auth/register"}>
              {isLoggedIn ? t`Check Your Score` : t`Check Your ATS Score`}
              <ArrowRight className="ml-2" weight="bold" />
            </Link>
          </Button>
        </motion.div>

        {/* Detail cards */}
        <div className="mx-auto mt-14 grid max-w-4xl gap-6 grid-cols-1 sm:grid-cols-3">
          {scoreDetails.map((detail, i) => (
            <motion.div
              key={detail.label}
              viewport={{ once: true }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0, transition: { delay: 0.05 * i } }}
              className="rounded-xl border border-border/50 bg-background p-5 text-center"
            >
              <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-lg bg-green-500/10 text-green-600">
                {detail.icon}
              </div>
              <h3 className="mt-3 text-sm font-semibold">{detail.label}</h3>
              <p className="mt-1.5 text-xs leading-relaxed text-foreground/50">{detail.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
